import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError } from "axios";
import { jwtHelper } from "../../helpers/JwtHelper";
import { axiosApi } from "../../interceptors/axios";
import { Result } from "../../types/Result";
import isIdyaResult from "../../helpers/isIdyaResult";

type UserToken = {
  token: string;
  refreshToken: string;
};

interface AuthState {
  token: string;
  username: string;
  isLoggedIn: boolean;
  loading: boolean;
  errors: string[];
  email: string;
  id: string;
  roles: string[];
  referenceCode: string;
  exp?: number;
}

const initialState: AuthState = {
  token: "",
  username: "",
  isLoggedIn: false,
  loading: false,
  errors: [],
  email: "",
  id: "",
  roles: [],
  referenceCode: "",
};

export const logOutAsync = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Logout starting...");
      // Önce API çağrısı yap
      await axiosApi.post("api/Auth/logout");

      // Tüm storage'i temizle
      await Promise.all([
        localStorage.removeItem("jetToken"),
        localStorage.removeItem("refreshToken"),
      ]);

      // iOS için küçük bir gecikme
      await new Promise((resolve) => setTimeout(resolve, 100));

      console.log("Logout completed successfully");
      return true;
    } catch (error) {
      console.error("Logout error:", error);
      // API hatası olsa bile storage'i temizle
      await Promise.all([
        localStorage.removeItem("jetToken"),
        localStorage.removeItem("refreshToken"),
      ]);

      // iOS için küçük bir gecikme
      await new Promise((resolve) => setTimeout(resolve, 100));

      console.log("Logout completed with error but storage cleared");
      return true; // Yine de başarılı dön ki state temizlensin
    }
  }
);

export const refreshTokenAsync = createAsyncThunk(
  "auth/refreshToken",
  async (_, thunkAPI) => {
    try {
      const refreshToken = await localStorage.getItem("refreshToken");
      if (!refreshToken)
        return thunkAPI.rejectWithValue("refreshToken token bulunamadı");
      const response = await axiosApi.post<Result<UserToken>>(
        "api/Auth/refresh-token",
        { refreshToken }
      );
      return response.data;
    } catch (error) {
      if (
        error instanceof AxiosError &&
        error.response &&
        isIdyaResult(error.response?.data)
      ) {
        return thunkAPI.rejectWithValue(error.response.data);
      }
      return thunkAPI.rejectWithValue("failed");
    }
  }
);

// Async thunk for login
export const loginAsync = createAsyncThunk(
  "auth/login",
  async (credentials: { email: string; password: string }, thunkAPI) => {
    try {
      const response = await axiosApi.post<Result<UserToken>>(
        "/api/Auth/login",
        null,
        {
          params: {
            email: credentials.email,
            password: credentials.password,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError)
        return thunkAPI.rejectWithValue(error.response?.data.errors);
      return thunkAPI.rejectWithValue("failed");
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ username: string; password: string }>
    ) => {
      state.username = action.payload.username;
    },
    logout: (state) => {
      state.username = "";
      state.isLoggedIn = false;
    },
    restoreToken: (state, action: PayloadAction<string>) => {
      if (
        action.payload &&
        jwtHelper.isTokenValid(action.payload, (token) => {
          state.username =
            token["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"];
          state.id =
            token[
              "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
            ];
          state.exp = token.exp;
          if (
            token[
              "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
            ]
          ) {
            if (
              Array.isArray(
                token[
                  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                ]
              )
            )
              state.roles =
                token[
                  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                ];
            else {
              state.roles = [
                token[
                  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                ],
              ];
            }
          }
          state.referenceCode = token.referenceCode;
          state.email =
            token[
              "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
            ];
        })
      ) {
        state.isLoggedIn = true;
        state.token = action.payload;
        localStorage.setItem("jwtToken", action.payload);
      } else {
        localStorage.removeItem("jwtToken");
      }
      state.errors = [];
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginAsync.pending, (state) => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(loginAsync.fulfilled, (state, action) => {
        if (
          action.payload.data &&
          jwtHelper.isTokenValid(action.payload.data.token, (token) => {
            state.username =
              token[
                "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
              ];
            state.id =
              token[
                "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
              ];
            state.exp = token.exp;
            if (
              token[
                "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
              ]
            ) {
              if (
                Array.isArray(
                  token[
                    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                  ]
                )
              )
                state.roles =
                  token[
                    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                  ];
              else {
                state.roles = [
                  token[
                    "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                  ],
                ];
              }
            }
            state.referenceCode = token.referenceCode;
            state.email =
              token[
                "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
              ];
          })
        ) {
          state.isLoggedIn = true;
          state.token = action.payload.data.token;
          localStorage.setItem("jwtToken", action.payload.data.token);
          localStorage.setItem(
            "refreshToken",
            action.payload.data.refreshToken
          );
        } else {
          localStorage.removeItem("jwtToken");
          localStorage.removeItem("refreshToken");
        }
        state.errors = [];
        state.loading = false;
      })
      .addCase(loginAsync.rejected, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload)) state.errors = action.payload;
      })
      .addCase(refreshTokenAsync.pending, () => {})
      .addCase(refreshTokenAsync.fulfilled, (state, action) => {
        if (action.payload.data) {
          if (
            jwtHelper.isTokenValid(action.payload.data.token, (token) => {
              state.username =
                token[
                  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
                ];
              state.id =
                token[
                  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier"
                ];
              state.email =
                token[
                  "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
                ];
              if (
                token[
                  "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                ]
              ) {
                if (
                  Array.isArray(
                    token[
                      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                    ]
                  )
                )
                  state.roles =
                    token[
                      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                    ];
                else {
                  state.roles = [
                    token[
                      "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
                    ],
                  ];
                }
              }
              state.exp = token.exp;
              state.referenceCode = token.referenceCode;
            })
          ) {
            state.isLoggedIn = true;
            state.token = action.payload.data.token;
            localStorage.setItem("jwtToken", action.payload.data.token);
            localStorage.setItem(
              "refreshToken",
              action.payload.data.refreshToken
            );
          }
        }
      })
      .addCase(refreshTokenAsync.rejected, () => {})
      // Logout cases
      .addCase(logOutAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(logOutAsync.fulfilled, (state) => {
        // State'i tamamen temizle
        return {
          ...initialState,
          loading: false,
        };
      })
      .addCase(logOutAsync.rejected, (state) => {
        // Hata durumunda da state'i temizle
        return {
          ...initialState,
          loading: false,
        };
      });
  },
});

export const { setCredentials, logout, restoreToken } = authSlice.actions;
export default authSlice.reducer;
